/**
 * Gets today's date
 */
export const getToday = (): Date => {
  return new Date();
};

/**
 * Safely parses a date string to Date object
 * Returns null if the string is null, undefined, empty, or invalid
 * @param dateString - Date string to parse (e.g., "2025-11-24", "2025-11-24T00:00:00")
 * @returns Date object or null if invalid
 */
export const parseDateSafe = (
  dateString: string | null | undefined
): Date | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return !isNaN(date.getTime()) ? date : null;
};

/**
 * Converts Date to ISO string format (YYYY-MM-DD) using local timezone
 * This avoids timezone issues where dates shift by one day
 * @param date - Date object to convert
 * @returns ISO string format (YYYY-MM-DD) or null if invalid
 */
export const formatDateToISO = (date: Date | null): string | null => {
  if (!date || isNaN(date.getTime())) return null;
  // Use local timezone instead of UTC to avoid timezone issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Gets current year
 */
export const getCurrentYear = (): number => {
  return new Date().getFullYear();
};

/**
 * Gets date from one year ago
 * @param baseDate - Base date to calculate from (defaults to today)
 * @param includeTime - Whether to include time information from base date (defaults to false)
 */
export const getOneYearAgo = (
  baseDate?: Date,
  includeTime: boolean = false
): Date => {
  const today = baseDate || getToday();

  if (includeTime) {
    return new Date(
      today.getFullYear() - 1,
      today.getMonth(),
      today.getDate(),
      today.getHours(),
      today.getMinutes(),
      today.getSeconds(),
      today.getMilliseconds()
    );
  }

  return new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
};

/**
 * Gets date from specified years ago
 * @param years - Number of years to go back
 * @param baseDate - Base date to calculate from (defaults to today)
 * @param includeTime - Whether to include time information from base date (defaults to false)
 */
export const getYearsAgo = (
  years: number,
  baseDate?: Date,
  includeTime: boolean = false
): Date => {
  const today = baseDate || getToday();

  if (includeTime) {
    return new Date(
      today.getFullYear() - years,
      today.getMonth(),
      today.getDate(),
      today.getHours(),
      today.getMinutes(),
      today.getSeconds(),
      today.getMilliseconds()
    );
  }

  return new Date(
    today.getFullYear() - years,
    today.getMonth(),
    today.getDate()
  );
};

/**
 * Validates if a date is valid
 * @param date - Date to validate
 */
export const isValidDate = (date: any): boolean => {
  return date instanceof Date && !isNaN(date.getTime());
};

// Date Extension Functions
declare global {
  interface Date {
    /**
     * Gets start of day (00:00:00)
     */
    getStartOfDay(): Date;

    /**
     * Gets end of day (23:59:59)
     */
    getEndOfDay(): Date;

    /**
     * Gets start of month (00:00:00)
     */
    getStartOfMonth(): Date;

    /**
     * Formats date with time
     * @param time - Time string (e.g., "05:30:00")
     */
    withTime(time: string): Date;

    /**
     * Checks if date is today
     */
    isToday(): boolean;

    /**
     * Formats date to YYYY-MM-DD format (date only, no time)
     */
    toDateOnly(): string;
  }
}

// Extension implementations
Date.prototype.getStartOfDay = function (): Date {
  return new Date(this.getFullYear(), this.getMonth(), this.getDate(), 0, 0, 0);
};

Date.prototype.getEndOfDay = function (): Date {
  return new Date(
    this.getFullYear(),
    this.getMonth(),
    this.getDate(),
    23,
    59,
    59
  );
};

Date.prototype.getStartOfMonth = function (): Date {
  return new Date(this.getFullYear(), this.getMonth(), 1, 0, 0, 0);
};

Date.prototype.withTime = function (time: string): Date {
  const dd = this.getDate();
  const mm = this.getMonth() + 1;
  const yyyy = this.getFullYear();
  return new Date(`${mm}/${dd}/${yyyy} ${time}`);
};

Date.prototype.isToday = function (): boolean {
  const today = new Date();
  return (
    this.getDate() === today.getDate() &&
    this.getMonth() === today.getMonth() &&
    this.getFullYear() === today.getFullYear()
  );
};

Date.prototype.toDateOnly = function (): string {
  const year = this.getFullYear();
  const month = String(this.getMonth() + 1).padStart(2, "0");
  const day = String(this.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Date format utilities for working with dates
 */
export const dateFormat = {
  /**
   * Parses ISO date string (from API) to Date object
   * @param isoString - ISO date string (e.g., "2025-10-07T05:25:00.000Z")
   * @returns Date object or null if invalid
   */
  parseISO: (isoString: string): Date | null => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      return !isNaN(date.getTime()) ? date : null;
    } catch (error) {
      return null;
    }
  },

  /**
   * Formats Date object to "DD/MM/YYYY HH:MM" display format
   * @param date - Date object to format
   * @returns Formatted string (e.g., "07/10/2025 05:25") or empty string if invalid
   */
  toDisplay: (date: Date | null): string => {
    if (!date) return "";
    try {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
      return "";
    }
  },

  /**
   * Converts Date object to ISO string for API requests
   * Preserves local timezone by manually formatting instead of using toISOString()
   * @param date - Date object to convert
   * @returns ISO string or empty string if invalid
   */
  toISO: (date: Date | null): string => {
    if (!date) return "";
    try {
      // Format as ISO but preserve local timezone
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");

      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    } catch (error) {
      return "";
    }
  },

  /**
   * Parses display format "DD/MM/YYYY HH:MM" to Date object
   * @param displayStr - Display format string (e.g., "07/10/2025 05:25")
   * @returns Date object or null if invalid
   */
  parseDisplay: (displayStr: string): Date | null => {
    if (!displayStr) return null;
    try {
      const parts = displayStr.split(" ");
      if (parts.length !== 2) return null;

      const dateParts = parts[0].split("/");
      if (dateParts.length !== 3) return null;

      const [day, month, year] = dateParts;
      const [hours, minutes] = parts[1].split(":");

      return new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hours),
        parseInt(minutes)
      );
    } catch (error) {
      return null;
    }
  },
};
